import { IServerPagination } from "app/model"
import { getStore } from "app/store/store"
import { OrderAPI } from "generated-api"
import { normalizeSync } from "normalize-diacritics"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  ICreateOrderRepositoryParams,
  IOrder,
  IOrderDTO,
  IOrderFilters,
  IOrderRepository,
  IUpdateOrderRepositoryParams
} from "./model"

interface IGetOrdersParams {
  comparators: string[]
  match: "exact" | "contains" | "starts_with" | "ends_with"
  nameSearch: string | undefined
}

export const orderRPO: IOrderRepository = {
  getAllOrders(): Promise<IOrder[]> {
    return new Promise<IOrder[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.getApiOrderList()
          .then(response => resolve(response.map(o => getOrderFromDTO(o))))
          .catch((error: any) => {
            console.error("Server error while getting all orders", error)
            reject("order.get.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all orders", ex)
        reject("order.get.all.error.unexpected")
      }
    })
  },
  getOrder(orderId: number): Promise<IOrder> {
    return new Promise<IOrder>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.getApiOrder(orderId)
          .then(response => resolve(getOrderFromDTO(response as IOrderDTO)))
          .catch((error: any) => {
            console.error("Server error while getting order", error)
            reject("order.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting order", ex)
        reject("order.get.error.unexpected")
      }
    })
  },
  getOrders(pagination: IServerPagination, filters: IOrderFilters): Promise<IOrder[]> {
    return new Promise<IOrder[]>((resolve, reject) => {
      const sort: string[] = []
      const offset = pagination.page * pagination.pageSize
      const limit = pagination.pageSize
      if (pagination.orderBy) {
        sort.push(`${pagination.orderBy},${pagination.order || "asc"}`)
      }
      const params = getGetOrdersParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.getApiOrderList(
          offset,
          limit,
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          sort.length > 0 ? sort : undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators // comparators
        )
          .then(response => resolve(response.map(r => getOrderFromDTO(r))))
          .catch((error: any) => {
            console.error("Server error while getting orders", error)
            reject("order.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting orders", ex)
        reject("order.get.error.unexpected")
      }
    })
  },
  getOrdersById(ids: number[]): Promise<IOrder[]> {
    return new Promise<IOrder[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.getApiOrderList(
          0,
          undefined,
          undefined, // search
          undefined, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          undefined, // order by => string[] (fieldname,[asc|desc]+)
          undefined,
          undefined,
          typeof getStore().getState().user.user.agency_id === "number" ? getStore().getState().user.user.agency_id : undefined,
          undefined,
          ids
        )
          .then(response => resolve(response.map(r => getOrderFromDTO(r))))
          .catch((error: any) => {
            console.error("Server error while getting orders by ids", error)
            reject("order.get.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting orders by ids", ex)
        reject("order.get.by.ids.error.unexpected")
      }
    })
  },
  getOrdersCount(filters: IOrderFilters): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const params = getGetOrdersParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.getApiOrderCount(
          undefined, // no offset
          undefined, // no limit
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators // comparators
        )
          .then(response => resolve(response.count || 0))
          .catch((error: any) => {
            console.error("Server error while counting orders", error)
            reject("orders.count.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while counting orders", ex)
        reject("orders.count.error.unexpected")
      }
    })
  },
  createOrder(params: ICreateOrderRepositoryParams): Promise<IOrder> {
    return new Promise<IOrder>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.postApiOrder(params)
          .then(response => resolve(getOrderFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while creating order", error)
            reject("order.create.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating order", ex)
        reject("order.create.error.unexpected")
      }
    })
  },
  updateOrder(params: IUpdateOrderRepositoryParams): Promise<IOrder> {
    return new Promise<IOrder>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        OrderAPI.putApiOrder(params)
          .then(response => resolve(getOrderFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while updating order", error)
            reject("order.update.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while updating order", ex)
        reject("order.update.error.unexpected")
      }
    })
  }
}

function getOrderFromDTO(orderDTO: IOrderDTO): IOrder {
  return {
    ...orderDTO,
    normalizedName: orderDTO.name ? normalizeSync(orderDTO.name).toLowerCase() : ""
  }
}

function getGetOrdersParams(filters: IOrderFilters): IGetOrdersParams {
  const params: IGetOrdersParams = {
    comparators: [],
    match: "contains",
    nameSearch: filters.name || undefined
  }
  return params
}
